/**
 * Large deterministic mock dataset used to stress-test the map at realistic
 * scale (clustering, search, panel switching). Never used in normal browsing:
 * it is only generated when the home map is opened with `?stress=<count>`.
 *
 * The generator is seeded, so the same `?stress=` value always produces the
 * same listings — performance runs stay comparable between sessions.
 */
import type { Property, PropertyType, Purpose } from "@/lib/mock-data";
import type { ParkingListing, ParkingType, VehicleType } from "@/lib/parking-mock-data";

/** Metro anchors with a spread (deg) that roughly covers the built-up area. */
const CITIES = [
  { name: "Chennai", lat: 13.0827, lng: 80.2707, spread: 0.28 },
  { name: "Bengaluru", lat: 12.9716, lng: 77.5946, spread: 0.3 },
  { name: "Hyderabad", lat: 17.385, lng: 78.4867, spread: 0.28 },
  { name: "Coimbatore", lat: 11.0168, lng: 76.9558, spread: 0.2 },
  { name: "Madurai", lat: 9.9252, lng: 78.1198, spread: 0.16 },
  { name: "Mumbai", lat: 19.076, lng: 72.8777, spread: 0.25 },
  { name: "Pune", lat: 18.5204, lng: 73.8567, spread: 0.24 },
  { name: "Delhi", lat: 28.6139, lng: 77.209, spread: 0.32 },
] as const;

const AREAS = [
  "Anna Nagar",
  "T. Nagar",
  "Velachery",
  "Whitefield",
  "Indiranagar",
  "Gachibowli",
  "Kondapur",
  "RS Puram",
  "Peelamedu",
  "Andheri East",
  "Kothrud",
  "Dwarka",
];

const TYPES: PropertyType[] = [
  "house",
  "apartment",
  "villa",
  "land",
  "room",
  "office",
  "shop",
  "commercial",
  "warehouse",
];

const PURPOSES: Purpose[] = ["sale", "rent", "lease"];

const PARKING_TYPES: ParkingType[] = [
  "covered",
  "open",
  "basement",
  "multi_level",
  "street",
  "valet",
];

const VEHICLES: VehicleType[] = ["car", "bike", "car_bike", "ev", "heavy"];

const IMAGES = [
  "photo-1600585154340-be6161a56a0c",
  "photo-1560448204-e02f11c3d0e2",
  "photo-1580587771525-78b9dba3b914",
  "photo-1502672260266-1c1ef2d93688",
  "photo-1493809842364-78817add7ffb",
  "photo-1568605114967-8130f3a36994",
];

const img = (i: number) =>
  `https://images.unsplash.com/${IMAGES[i % IMAGES.length]}?auto=format&fit=crop&w=400&q=60`;

/** Mulberry32 — small, fast, deterministic PRNG. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const inr = (n: number) =>
  n >= 10000000
    ? `₹${(n / 10000000).toFixed(2)} Cr`
    : n >= 100000
      ? `₹${(n / 100000).toFixed(1)} L`
      : `₹${n.toLocaleString("en-IN")}`;

export interface StressDataset {
  properties: Property[];
  parking: ParkingListing[];
}

/** Roughly 25% of a stress run is parking so the "PP" layer is loaded too. */
export function generateStressDataset(count: number, seed = 42): StressDataset {
  const total = Math.max(0, Math.min(20000, Math.floor(count)));
  const parkingCount = Math.floor(total * 0.25);
  const propertyCount = total - parkingCount;
  const rand = rng(seed);

  const properties: Property[] = Array.from({ length: propertyCount }, (_, i) => {
    const city = CITIES[i % CITIES.length];
    const lat = city.lat + (rand() - 0.5) * city.spread;
    const lng = city.lng + (rand() - 0.5) * city.spread;
    const purpose = PURPOSES[i % PURPOSES.length];
    const type = TYPES[Math.floor(rand() * TYPES.length)];
    const beds = 1 + Math.floor(rand() * 4);
    const price =
      purpose === "sale"
        ? 2500000 + Math.floor(rand() * 45000000)
        : 8000 + Math.floor(rand() * 120000);
    const area = AREAS[Math.floor(rand() * AREAS.length)];
    return {
      id: `stress-p${i}`,
      title: `${beds}BHK ${type} in ${area}`,
      type,
      purpose,
      price,
      priceLabel: purpose === "sale" ? inr(price) : `${inr(price)} / mo`,
      location: `${area}, ${city.name}`,
      distanceKm: 0,
      bedrooms: beds,
      bathrooms: Math.max(1, beds - 1),
      balcony: Math.floor(rand() * 3),
      areaSqft: 450 + Math.floor(rand() * 3200),
      highlights: ["Sample data", "Stress test"],
      description: "Synthetic listing generated for map performance testing.",
      image: img(i),
      x: 10 + ((i * 37) % 80),
      y: 10 + ((i * 61) % 80),
      lat,
      lng,
      createdAt: new Date(Date.now() - i * 3600000).toISOString(),
    };
  });

  const parking: ParkingListing[] = Array.from({ length: parkingCount }, (_, i) => {
    const city = CITIES[(i + 3) % CITIES.length];
    const lat = city.lat + (rand() - 0.5) * city.spread;
    const lng = city.lng + (rand() - 0.5) * city.spread;
    const area = AREAS[Math.floor(rand() * AREAS.length)];
    const total_ = 10 + Math.floor(rand() * 200);
    return {
      id: `stress-pp${i}`,
      title: `${area} Parking Hub ${i + 1}`,
      providerName: "Landoo Demo Parking",
      address: `${area}, ${city.name}`,
      city: city.name,
      distanceKm: 0,
      locationPrecision: rand() > 0.5 ? "exact" : "approximate",
      parkingType: PARKING_TYPES[Math.floor(rand() * PARKING_TYPES.length)],
      vehicleTypes: [VEHICLES[Math.floor(rand() * VEHICLES.length)]],
      totalSlots: total_,
      availableSlots: Math.floor(rand() * total_),
      priceHourly: 20 + Math.floor(rand() * 60),
      priceDaily: 150 + Math.floor(rand() * 400),
      priceMonthly: 1500 + Math.floor(rand() * 6000),
      openingTime: null,
      closingTime: null,
      open24h: rand() > 0.4,
      cctv: rand() > 0.3,
      security: rand() > 0.4,
      lighting: rand() > 0.2,
      evCharging: rand() > 0.6,
      verified: rand() > 0.5,
      image: img(i + 2),
      description: "Synthetic parking facility generated for map performance testing.",
      bookingEnabled: rand() > 0.5,
      x: 10 + ((i * 53) % 80),
      y: 10 + ((i * 29) % 80),
      lat,
      lng,
      createdAt: new Date(Date.now() - i * 5400000).toISOString(),
    };
  });

  return { properties, parking };
}
